import React, { useCallback, useEffect, useState, createContext, useContext } from 'react';
import { supabase } from '../lib/supabase';
export type PackageCategory = 'hosting' | 'radio' | 'reseller' | 'shoutcast' | 'shoutcast-reseller' | 'domain';
export interface Package {
  id: string;
  category: PackageCategory;
  name: string;
  description: string;
  monthlyPrice: number;
  yearlyPrice: number;
  features: string[];
  highlighted: boolean;
  active: boolean;
  serverId?: string;
  serverPackageName?: string;
  provisionStatus?: 'pending' | 'provisioned' | 'failed' | 'not-linked';
}
export interface CartItem {
  packageId: string;
  name: string;
  category: PackageCategory;
  price: number;
  billing: 'monthly' | 'yearly';
  quantity: number;
}
export interface InvoiceItem {
  description: string;
  amount: number;
}
export interface Invoice {
  id: string;
  clientName: string;
  clientEmail: string;
  clientType: 'customer' | 'business';
  items: InvoiceItem[];
  total: number;
  status: 'paid' | 'pending' | 'overdue' | 'cancelled';
  createdAt: string;
  dueAt: string;
  paidAt?: string;
}
export interface TicketMessage {
  id: string;
  sender: string;
  role: 'client' | 'staff';
  content: string;
  createdAt: string;
}
export interface Ticket {
  id: string;
  subject: string;
  clientName: string;
  clientEmail: string;
  priority: 'low' | 'medium' | 'high' | 'critical';
  status: 'open' | 'in-progress' | 'resolved' | 'closed';
  assignedTo?: string;
  department?: string;
  messages: TicketMessage[];
  createdAt: string;
  updatedAt: string;
}
export interface Staff {
  id: string;
  name: string;
  email: string;
  role: 'admin' | 'support' | 'billing' | 'technician';
  status: 'active' | 'inactive';
  avatar?: string;
  permissions: string[];
  createdAt: string;
}
export type ServerType = 'cpanel' | 'sonicpanel' | 'plesk' | 'directadmin' | 'cyberpanel' | 'cloudpanel' | 'virtualmin' | 'ispconfig' | 'centova' | 'whmcs';
export interface ServerConnection {
  id: string;
  name: string;
  type: ServerType;
  hostname: string;
  port: number;
  apiKey: string;
  username: string;
  status: 'connected' | 'disconnected' | 'error';
  lastSync: string;
  notes: string;
  stats?: {
    accounts: number;
    diskUsage: string;
    bandwidth: string;
  };
}
export interface CustomerService {
  packageId: string;
  packageName: string;
  category: PackageCategory;
  status: 'active' | 'suspended' | 'cancelled' | 'pending';
  billingCycle: 'monthly' | 'yearly';
  price: number;
  nextRenewal: string;
  domain?: string;
  serverType?: ServerType;
  controlPanelUrl?: string;
  controlPanelUsername?: string;
  diskUsageMb?: number;
  diskLimitMb?: number;
  bandwidthUsedMb?: number;
  bandwidthLimitMb?: number;
  emailAccounts?: number;
  currentListeners?: number;
  maxListeners?: number;
  bitrate?: number;
  streamUrl?: string;
  streamStatus?: 'online' | 'offline';
}
export interface Customer {
  id: string;
  name: string;
  email: string;
  phone: string;
  company?: string;
  type: 'customer' | 'business';
  status: 'active' | 'suspended' | 'cancelled';
  services: CustomerService[];
  totalSpent: number;
  createdAt: string;
  lastLogin: string;
  notes?: string;
}
export interface TLD {
  id: string;
  extension: string;
  registrarId: string;
  registerPrice: number;
  renewPrice: number;
  transferPrice: number;
  category: 'popular' | 'country' | 'new' | 'specialty';
  whoisPrivacy: boolean;
  active: boolean;
}
export interface DomainRegistrar {
  id: string;
  name: string;
  apiEndpoint: string;
  apiKey: string;
  status: 'connected' | 'disconnected' | 'error';
  supportedTlds: string[];
}
// ─── Supabase row ↔ app model converters ───
function rowToPackage(r: any): Package {
  return {
    id: r.id,
    category: r.category,
    name: r.name,
    description: r.description,
    monthlyPrice: Number(r.monthly_price),
    yearlyPrice: Number(r.yearly_price),
    features: r.features || [],
    highlighted: r.highlighted,
    active: r.active,
    serverId: r.server_id || undefined,
    serverPackageName: r.server_package_name || undefined,
    provisionStatus: r.provision_status || 'not-linked'
  };
}
function packageToRow(p: Package) {
  return {
    id: p.id,
    category: p.category,
    name: p.name,
    description: p.description,
    monthly_price: p.monthlyPrice,
    yearly_price: p.yearlyPrice,
    features: p.features,
    highlighted: p.highlighted,
    active: p.active,
    server_id: p.serverId || null,
    server_package_name: p.serverPackageName || null,
    provision_status: p.provisionStatus || 'not-linked'
  };
}
function rowToInvoice(r: any): Invoice {
  return {
    id: r.id,
    clientName: r.client_name,
    clientEmail: r.client_email,
    clientType: r.client_type,
    items: r.items || [],
    total: Number(r.total),
    status: r.status,
    createdAt: r.created_at,
    dueAt: r.due_at,
    paidAt: r.paid_at || undefined
  };
}
function invoiceToRow(i: Invoice) {
  return {
    id: i.id,
    client_name: i.clientName,
    client_email: i.clientEmail,
    client_type: i.clientType,
    items: i.items,
    total: i.total,
    status: i.status,
    created_at: i.createdAt,
    due_at: i.dueAt,
    paid_at: i.paidAt || null
  };
}
function rowToTicket(r: any): Ticket {
  return {
    id: r.id,
    subject: r.subject,
    clientName: r.client_name,
    clientEmail: r.client_email,
    priority: r.priority,
    status: r.status,
    assignedTo: r.assigned_to || undefined,
    department: r.department || undefined,
    messages: r.messages || [],
    createdAt: r.created_at,
    updatedAt: r.updated_at
  };
}
function ticketToRow(t: Ticket) {
  return {
    id: t.id,
    subject: t.subject,
    client_name: t.clientName,
    client_email: t.clientEmail,
    priority: t.priority,
    status: t.status,
    assigned_to: t.assignedTo || null,
    department: t.department || null,
    messages: t.messages,
    created_at: t.createdAt,
    updated_at: t.updatedAt
  };
}
function rowToStaff(r: any): Staff {
  return {
    id: r.id,
    name: r.name,
    email: r.email,
    role: r.role,
    status: r.status,
    avatar: r.avatar || undefined,
    permissions: r.permissions || [],
    createdAt: r.created_at
  };
}
function staffToRow(s: Staff) {
  return {
    id: s.id,
    name: s.name,
    email: s.email,
    role: s.role,
    status: s.status,
    avatar: s.avatar || null,
    permissions: s.permissions,
    created_at: s.createdAt
  };
}
function rowToServer(r: any): ServerConnection {
  return {
    id: r.id,
    name: r.name,
    type: r.type,
    hostname: r.hostname,
    port: r.port,
    apiKey: r.api_key,
    username: r.username || '',
    status: r.status,
    lastSync: r.last_sync,
    notes: r.notes || '',
    stats: r.stats || undefined
  };
}
function serverToRow(s: ServerConnection) {
  return {
    id: s.id,
    name: s.name,
    type: s.type,
    hostname: s.hostname,
    port: s.port,
    api_key: s.apiKey,
    username: s.username,
    status: s.status,
    last_sync: s.lastSync,
    notes: s.notes,
    stats: s.stats || null
  };
}
function rowToCustomer(r: any): Customer {
  return {
    id: r.id,
    name: r.name,
    email: r.email,
    phone: r.phone || '',
    company: r.company || undefined,
    type: r.type,
    status: r.status,
    services: r.services || [],
    totalSpent: Number(r.total_spent),
    createdAt: r.created_at,
    lastLogin: r.last_login,
    notes: r.notes || undefined
  };
}
function customerToRow(c: Customer) {
  return {
    id: c.id,
    name: c.name,
    email: c.email,
    phone: c.phone,
    company: c.company || null,
    type: c.type,
    status: c.status,
    services: c.services,
    total_spent: c.totalSpent,
    created_at: c.createdAt,
    last_login: c.lastLogin,
    notes: c.notes || null
  };
}
function rowToTld(r: any): TLD {
  return {
    id: r.id,
    extension: r.extension,
    registrarId: r.registrar_id || '',
    registerPrice: Number(r.register_price),
    renewPrice: Number(r.renew_price),
    transferPrice: Number(r.transfer_price),
    category: r.category,
    whoisPrivacy: r.whois_privacy,
    active: r.active
  };
}
function tldToRow(t: TLD) {
  return {
    id: t.id,
    extension: t.extension,
    registrar_id: t.registrarId || null,
    register_price: t.registerPrice,
    renew_price: t.renewPrice,
    transfer_price: t.transferPrice,
    category: t.category,
    whois_privacy: t.whoisPrivacy,
    active: t.active
  };
}
function rowToRegistrar(r: any): DomainRegistrar {
  return {
    id: r.id,
    name: r.name,
    apiEndpoint: r.api_endpoint,
    apiKey: r.api_key,
    status: r.status,
    supportedTlds: r.supported_tlds || []
  };
}
function registrarToRow(r: DomainRegistrar) {
  return {
    id: r.id,
    name: r.name,
    api_endpoint: r.apiEndpoint,
    api_key: r.apiKey,
    status: r.status,
    supported_tlds: r.supportedTlds
  };
}
// ─── Context type ───
interface AppContextType {
  packages: Package[];
  addPackage: (pkg: Package) => void;
  updatePackage: (id: string, pkg: Partial<Package>) => void;
  deletePackage: (id: string) => void;
  cart: CartItem[];
  addToCart: (item: CartItem) => void;
  removeFromCart: (packageId: string) => void;
  updateCartQuantity: (packageId: string, quantity: number) => void;
  clearCart: () => void;
  cartTotal: number;
  invoices: Invoice[];
  addInvoice: (inv: Invoice) => void;
  updateInvoice: (id: string, inv: Partial<Invoice>) => void;
  tickets: Ticket[];
  addTicket: (ticket: Ticket) => void;
  updateTicket: (id: string, ticket: Partial<Ticket>) => void;
  staff: Staff[];
  addStaff: (member: Staff) => void;
  updateStaff: (id: string, member: Partial<Staff>) => void;
  deleteStaff: (id: string) => void;
  serverConnections: ServerConnection[];
  addServerConnection: (conn: ServerConnection) => void;
  updateServerConnection: (id: string, conn: Partial<ServerConnection>) => void;
  deleteServerConnection: (id: string) => void;
  customers: Customer[];
  addCustomer: (customer: Customer) => void;
  updateCustomer: (id: string, customer: Partial<Customer>) => void;
  deleteCustomer: (id: string) => void;
  tlds: TLD[];
  addTld: (tld: TLD) => void;
  updateTld: (id: string, tld: Partial<TLD>) => void;
  deleteTld: (id: string) => void;
  domainRegistrars: DomainRegistrar[];
  addDomainRegistrar: (reg: DomainRegistrar) => void;
  updateDomainRegistrar: (id: string, reg: Partial<DomainRegistrar>) => void;
  deleteDomainRegistrar: (id: string) => void;
  isLoading: boolean;
}
const AppContext = createContext<AppContextType | undefined>(undefined);
export function AppProvider({
  children
}: {
  children: ReactNode;
}) {
  const [isLoading, setIsLoading] = useState(true);
  const [packages, setPackages] = useState<Package[]>([]);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [staff, setStaff] = useState<Staff[]>([]);
  const [serverConnections, setServerConnections] = useState<ServerConnection[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [tlds, setTlds] = useState<TLD[]>([]);
  const [domainRegistrars, setDomainRegistrars] = useState<DomainRegistrar[]>([]);
  // ─── Initial data fetch ───
  useEffect(() => {
    async function fetchAll() {
      setIsLoading(true);
      try {
        const [pkgRes, invRes, tktRes, staffRes, srvRes, custRes, tldRes, regRes] = await Promise.all([supabase.from('packages').select('*').then(res => {
          if (res.error) console.warn('packages fetch error:', res.error.message);
          return res;
        }), supabase.from('invoices').select('*').then(res => {
          if (res.error) console.warn('invoices fetch error:', res.error.message);
          return res;
        }), supabase.from('tickets').select('*').then(res => {
          if (res.error) console.warn('tickets fetch error:', res.error.message);
          return res;
        }), supabase.from('staff').select('*').then(res => {
          if (res.error) console.warn('staff fetch error:', res.error.message);
          return res;
        }), supabase.from('server_connections').select('*').then(res => {
          if (res.error) console.warn('server_connections fetch error:', res.error.message);
          return res;
        }), supabase.from('customers').select('*').then(res => {
          if (res.error) console.warn('customers fetch error:', res.error.message);
          return res;
        }), supabase.from('tlds').select('*').then(res => {
          if (res.error) console.warn('tlds fetch error:', res.error.message);
          return res;
        }), supabase.from('domain_registrars').select('*').then(res => {
          if (res.error) console.warn('domain_registrars fetch error:', res.error.message);
          return res;
        })]);
        if (pkgRes.data) setPackages(pkgRes.data.map(rowToPackage));
        if (invRes.data) setInvoices(invRes.data.map(rowToInvoice));
        if (tktRes.data) setTickets(tktRes.data.map(rowToTicket));
        if (staffRes.data) setStaff(staffRes.data.map(rowToStaff));
        if (srvRes.data) setServerConnections(srvRes.data.map(rowToServer));
        if (custRes.data) setCustomers(custRes.data.map(rowToCustomer));
        if (tldRes.data) setTlds(tldRes.data.map(rowToTld));
        if (regRes.data) setDomainRegistrars(regRes.data.map(rowToRegistrar));
      } catch (err) {
        console.error('AppContext fetchAll failed:', err);
      } finally {
        setIsLoading(false);
      }
    }
    fetchAll();
  }, []);
  // ─── Real-time subscriptions ───
  useEffect(() => {
    const channels = [supabase.channel('packages-changes').on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'packages'
    }, payload => {
      if (payload.eventType === 'INSERT') {
        setPackages(prev => [...prev, rowToPackage(payload.new)]);
      } else if (payload.eventType === 'UPDATE') {
        setPackages(prev => prev.map(p => p.id === payload.new.id ? rowToPackage(payload.new) : p));
      } else if (payload.eventType === 'DELETE') {
        setPackages(prev => prev.filter(p => p.id !== payload.old.id));
      }
    }).subscribe(), supabase.channel('invoices-changes').on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'invoices'
    }, payload => {
      if (payload.eventType === 'INSERT') {
        setInvoices(prev => [...prev, rowToInvoice(payload.new)]);
      } else if (payload.eventType === 'UPDATE') {
        setInvoices(prev => prev.map(i => i.id === payload.new.id ? rowToInvoice(payload.new) : i));
      } else if (payload.eventType === 'DELETE') {
        setInvoices(prev => prev.filter(i => i.id !== payload.old.id));
      }
    }).subscribe(), supabase.channel('tickets-changes').on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'tickets'
    }, payload => {
      if (payload.eventType === 'INSERT') {
        setTickets(prev => [...prev, rowToTicket(payload.new)]);
      } else if (payload.eventType === 'UPDATE') {
        setTickets(prev => prev.map(t => t.id === payload.new.id ? rowToTicket(payload.new) : t));
      } else if (payload.eventType === 'DELETE') {
        setTickets(prev => prev.filter(t => t.id !== payload.old.id));
      }
    }).subscribe(), supabase.channel('staff-changes').on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'staff'
    }, payload => {
      if (payload.eventType === 'INSERT') {
        setStaff(prev => [...prev, rowToStaff(payload.new)]);
      } else if (payload.eventType === 'UPDATE') {
        setStaff(prev => prev.map(s => s.id === payload.new.id ? rowToStaff(payload.new) : s));
      } else if (payload.eventType === 'DELETE') {
        setStaff(prev => prev.filter(s => s.id !== payload.old.id));
      }
    }).subscribe(), supabase.channel('servers-changes').on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'server_connections'
    }, payload => {
      if (payload.eventType === 'INSERT') {
        setServerConnections(prev => [...prev, rowToServer(payload.new)]);
      } else if (payload.eventType === 'UPDATE') {
        setServerConnections(prev => prev.map(s => s.id === payload.new.id ? rowToServer(payload.new) : s));
      } else if (payload.eventType === 'DELETE') {
        setServerConnections(prev => prev.filter(s => s.id !== payload.old.id));
      }
    }).subscribe(), supabase.channel('customers-changes').on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'customers'
    }, payload => {
      if (payload.eventType === 'INSERT') {
        setCustomers(prev => [...prev, rowToCustomer(payload.new)]);
      } else if (payload.eventType === 'UPDATE') {
        setCustomers(prev => prev.map(c => c.id === payload.new.id ? rowToCustomer(payload.new) : c));
      } else if (payload.eventType === 'DELETE') {
        setCustomers(prev => prev.filter(c => c.id !== payload.old.id));
      }
    }).subscribe(), supabase.channel('tlds-changes').on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'tlds'
    }, payload => {
      if (payload.eventType === 'INSERT') {
        setTlds(prev => [...prev, rowToTld(payload.new)]);
      } else if (payload.eventType === 'UPDATE') {
        setTlds(prev => prev.map(t => t.id === payload.new.id ? rowToTld(payload.new) : t));
      } else if (payload.eventType === 'DELETE') {
        setTlds(prev => prev.filter(t => t.id !== payload.old.id));
      }
    }).subscribe(), supabase.channel('registrars-changes').on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'domain_registrars'
    }, payload => {
      if (payload.eventType === 'INSERT') {
        setDomainRegistrars(prev => [...prev, rowToRegistrar(payload.new)]);
      } else if (payload.eventType === 'UPDATE') {
        setDomainRegistrars(prev => prev.map(r => r.id === payload.new.id ? rowToRegistrar(payload.new) : r));
      } else if (payload.eventType === 'DELETE') {
        setDomainRegistrars(prev => prev.filter(r => r.id !== payload.old.id));
      }
    }).subscribe()];
    return () => {
      channels.forEach(ch => supabase.removeChannel(ch));
    };
  }, []);
  // ─── Packages (Supabase) ───
  const addPackage = async (pkg: Package) => {
    setPackages(prev => [...prev, pkg]);
    await supabase.from('packages').insert(packageToRow(pkg));
  };
  const updatePackage = async (id: string, pkg: Partial<Package>) => {
    setPackages(prev => prev.map(p => p.id === id ? {
      ...p,
      ...pkg
    } : p));
    const row: any = {};
    if (pkg.category !== undefined) row.category = pkg.category;
    if (pkg.name !== undefined) row.name = pkg.name;
    if (pkg.description !== undefined) row.description = pkg.description;
    if (pkg.monthlyPrice !== undefined) row.monthly_price = pkg.monthlyPrice;
    if (pkg.yearlyPrice !== undefined) row.yearly_price = pkg.yearlyPrice;
    if (pkg.features !== undefined) row.features = pkg.features;
    if (pkg.highlighted !== undefined) row.highlighted = pkg.highlighted;
    if (pkg.active !== undefined) row.active = pkg.active;
    if (pkg.serverId !== undefined) row.server_id = pkg.serverId;
    if (pkg.serverPackageName !== undefined) row.server_package_name = pkg.serverPackageName;
    if (pkg.provisionStatus !== undefined) row.provision_status = pkg.provisionStatus;
    await supabase.from('packages').update(row).eq('id', id);
  };
  const deletePackage = async (id: string) => {
    setPackages(prev => prev.filter(p => p.id !== id));
    await supabase.from('packages').delete().eq('id', id);
  };
  // ─── Cart (local only — per session) ───
  const addToCart = (item: CartItem) => {
    setCart(prev => {
      const existing = prev.find(c => c.packageId === item.packageId && c.billing === item.billing);
      if (existing) {
        return prev.map(c => c.packageId === item.packageId && c.billing === item.billing ? {
          ...c,
          quantity: c.quantity + 1
        } : c);
      }
      return [...prev, {
        ...item,
        quantity: 1
      }];
    });
  };
  const removeFromCart = (packageId: string) => setCart(prev => prev.filter(c => c.packageId !== packageId));
  const updateCartQuantity = (packageId: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(packageId);
      return;
    }
    setCart(prev => prev.map(c => c.packageId === packageId ? {
      ...c,
      quantity
    } : c));
  };
  const clearCart = () => setCart([]);
  const cartTotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  // ─── Invoices (Supabase) ───
  const addInvoice = async (inv: Invoice) => {
    setInvoices(prev => [...prev, inv]);
    await supabase.from('invoices').insert(invoiceToRow(inv));
  };
  const updateInvoice = async (id: string, inv: Partial<Invoice>) => {
    setInvoices(prev => prev.map(i => i.id === id ? {
      ...i,
      ...inv
    } : i));
    const row: any = {};
    if (inv.clientName !== undefined) row.client_name = inv.clientName;
    if (inv.clientEmail !== undefined) row.client_email = inv.clientEmail;
    if (inv.clientType !== undefined) row.client_type = inv.clientType;
    if (inv.items !== undefined) row.items = inv.items;
    if (inv.total !== undefined) row.total = inv.total;
    if (inv.status !== undefined) row.status = inv.status;
    if (inv.createdAt !== undefined) row.created_at = inv.createdAt;
    if (inv.dueAt !== undefined) row.due_at = inv.dueAt;
    if (inv.paidAt !== undefined) row.paid_at = inv.paidAt;
    await supabase.from('invoices').update(row).eq('id', id);
  };
  // ─── Tickets (Supabase) ───
  const addTicket = async (ticket: Ticket) => {
    setTickets(prev => [...prev, ticket]);
    await supabase.from('tickets').insert(ticketToRow(ticket));
  };
  const updateTicket = async (id: string, ticket: Partial<Ticket>) => {
    setTickets(prev => prev.map(t => t.id === id ? {
      ...t,
      ...ticket
    } : t));
    const row: any = {};
    if (ticket.subject !== undefined) row.subject = ticket.subject;
    if (ticket.clientName !== undefined) row.client_name = ticket.clientName;
    if (ticket.clientEmail !== undefined) row.client_email = ticket.clientEmail;
    if (ticket.priority !== undefined) row.priority = ticket.priority;
    if (ticket.status !== undefined) row.status = ticket.status;
    if (ticket.assignedTo !== undefined) row.assigned_to = ticket.assignedTo;
    if (ticket.department !== undefined) row.department = ticket.department;
    if (ticket.messages !== undefined) row.messages = ticket.messages;
    if (ticket.updatedAt !== undefined) row.updated_at = ticket.updatedAt;
    await supabase.from('tickets').update(row).eq('id', id);
  };
  // ─── Staff (Supabase) ───
  const addStaff = async (member: Staff) => {
    setStaff(prev => [...prev, member]);
    await supabase.from('staff').insert(staffToRow(member));
  };
  const updateStaff = async (id: string, member: Partial<Staff>) => {
    setStaff(prev => prev.map(s => s.id === id ? {
      ...s,
      ...member
    } : s));
    const row: any = {};
    if (member.name !== undefined) row.name = member.name;
    if (member.email !== undefined) row.email = member.email;
    if (member.role !== undefined) row.role = member.role;
    if (member.status !== undefined) row.status = member.status;
    if (member.permissions !== undefined) row.permissions = member.permissions;
    await supabase.from('staff').update(row).eq('id', id);
  };
  const deleteStaff = async (id: string) => {
    setStaff(prev => prev.filter(s => s.id !== id));
    await supabase.from('staff').delete().eq('id', id);
  };
  // ─── Server Connections (Supabase) ───
  const addServerConnection = async (conn: ServerConnection) => {
    setServerConnections(prev => [...prev, conn]);
    await supabase.from('server_connections').insert(serverToRow(conn));
  };
  const updateServerConnection = async (id: string, conn: Partial<ServerConnection>) => {
    setServerConnections(prev => prev.map(c => c.id === id ? {
      ...c,
      ...conn
    } : c));
    const row: any = {};
    if (conn.name !== undefined) row.name = conn.name;
    if (conn.type !== undefined) row.type = conn.type;
    if (conn.hostname !== undefined) row.hostname = conn.hostname;
    if (conn.port !== undefined) row.port = conn.port;
    if (conn.apiKey !== undefined) row.api_key = conn.apiKey;
    if (conn.username !== undefined) row.username = conn.username;
    if (conn.status !== undefined) row.status = conn.status;
    if (conn.lastSync !== undefined) row.last_sync = conn.lastSync;
    if (conn.notes !== undefined) row.notes = conn.notes;
    if (conn.stats !== undefined) row.stats = conn.stats;
    await supabase.from('server_connections').update(row).eq('id', id);
  };
  const deleteServerConnection = async (id: string) => {
    setServerConnections(prev => prev.filter(c => c.id !== id));
    await supabase.from('server_connections').delete().eq('id', id);
  };
  // ─── Customers (Supabase) ───
  const addCustomer = async (customer: Customer) => {
    setCustomers(prev => [...prev, customer]);
    await supabase.from('customers').insert(customerToRow(customer));
  };
  const updateCustomer = async (id: string, customer: Partial<Customer>) => {
    setCustomers(prev => prev.map(c => c.id === id ? {
      ...c,
      ...customer
    } : c));
    const row: any = {};
    if (customer.name !== undefined) row.name = customer.name;
    if (customer.email !== undefined) row.email = customer.email;
    if (customer.phone !== undefined) row.phone = customer.phone;
    if (customer.company !== undefined) row.company = customer.company;
    if (customer.type !== undefined) row.type = customer.type;
    if (customer.status !== undefined) row.status = customer.status;
    if (customer.services !== undefined) row.services = customer.services;
    if (customer.totalSpent !== undefined) row.total_spent = customer.totalSpent;
    if (customer.notes !== undefined) row.notes = customer.notes;
    await supabase.from('customers').update(row).eq('id', id);
  };
  const deleteCustomer = async (id: string) => {
    setCustomers(prev => prev.filter(c => c.id !== id));
    await supabase.from('customers').delete().eq('id', id);
  };
  // ─── TLDs (Supabase) ───
  const addTld = async (tld: TLD) => {
    setTlds(prev => [...prev, tld]);
    await supabase.from('tlds').insert(tldToRow(tld));
  };
  const updateTld = async (id: string, tld: Partial<TLD>) => {
    setTlds(prev => prev.map(t => t.id === id ? {
      ...t,
      ...tld
    } : t));
    const row: any = {};
    if (tld.extension !== undefined) row.extension = tld.extension;
    if (tld.registrarId !== undefined) row.registrar_id = tld.registrarId;
    if (tld.registerPrice !== undefined) row.register_price = tld.registerPrice;
    if (tld.renewPrice !== undefined) row.renew_price = tld.renewPrice;
    if (tld.transferPrice !== undefined) row.transfer_price = tld.transferPrice;
    if (tld.category !== undefined) row.category = tld.category;
    if (tld.whoisPrivacy !== undefined) row.whois_privacy = tld.whoisPrivacy;
    if (tld.active !== undefined) row.active = tld.active;
    await supabase.from('tlds').update(row).eq('id', id);
  };
  const deleteTld = async (id: string) => {
    setTlds(prev => prev.filter(t => t.id !== id));
    await supabase.from('tlds').delete().eq('id', id);
  };
  // ─── Domain Registrars (Supabase) ───
  const addDomainRegistrar = async (reg: DomainRegistrar) => {
    setDomainRegistrars(prev => [...prev, reg]);
    await supabase.from('domain_registrars').insert(registrarToRow(reg));
  };
  const updateDomainRegistrar = async (id: string, reg: Partial<DomainRegistrar>) => {
    setDomainRegistrars(prev => prev.map(r => r.id === id ? {
      ...r,
      ...reg
    } : r));
    const row: any = {};
    if (reg.name !== undefined) row.name = reg.name;
    if (reg.apiEndpoint !== undefined) row.api_endpoint = reg.apiEndpoint;
    if (reg.apiKey !== undefined) row.api_key = reg.apiKey;
    if (reg.status !== undefined) row.status = reg.status;
    if (reg.supportedTlds !== undefined) row.supported_tlds = reg.supportedTlds;
    await supabase.from('domain_registrars').update(row).eq('id', id);
  };
  const deleteDomainRegistrar = async (id: string) => {
    setDomainRegistrars(prev => prev.filter(r => r.id !== id));
    await supabase.from('domain_registrars').delete().eq('id', id);
  };
  return <AppContext.Provider value={{
    packages,
    addPackage,
    updatePackage,
    deletePackage,
    cart,
    addToCart,
    removeFromCart,
    updateCartQuantity,
    clearCart,
    cartTotal,
    invoices,
    addInvoice,
    updateInvoice,
    tickets,
    addTicket,
    updateTicket,
    staff,
    addStaff,
    updateStaff,
    deleteStaff,
    serverConnections,
    addServerConnection,
    updateServerConnection,
    deleteServerConnection,
    customers,
    addCustomer,
    updateCustomer,
    deleteCustomer,
    tlds,
    addTld,
    updateTld,
    deleteTld,
    domainRegistrars,
    addDomainRegistrar,
    updateDomainRegistrar,
    deleteDomainRegistrar,
    isLoading
  }} data-id="element-1993">
      {children}
    </AppContext.Provider>;
}
export function useAppContext() {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
}